export type ResetErrorView =
  /** The server read the request and said no. `detail` is its reason, when it gave one. */
  | { kind: 'refused'; detail?: string }
  /** Rate limited: the fix is to wait, not to retype anything. */
  | { kind: 'wait'; waitMinutes?: number }
  /** No usable answer — server error, proxy page, or no response. Says nothing about the code. */
  | { kind: 'unavailable' }

/**
 * Decide how a failed password-reset call should read to the user.
 *
 * #211: the page showed `data.error`, else "invalid code or request expired". The server sends
 * `error` only from its account and code checks. A refused new password answers
 * `{new_password: [...]}`, the per-IP throttle `{detail}`, and a deploy's API restart a proxy
 * 502 — all three were reported to the user as a bad code.
 */
export function presentResetError(status: number | undefined, data?: unknown): ResetErrorView {
  // #240: the reCAPTCHA check being unreachable is its own 503 with its own sentence.
  if (status === 503 && (data as { error_code?: unknown } | undefined)?.error_code === 'CAPTCHA_UNAVAILABLE') {
    return { kind: 'refused', detail: serverText(data) }
  }
  if (status === undefined || status >= 500) return { kind: 'unavailable' }
  if (status === 429) return { kind: 'wait', waitMinutes: throttleMinutes(data) }
  return { kind: 'refused', detail: serverText(data) }
}

/** The six digits of a reset code, however it was pasted ("123 456", line breaks, dashes). */
export function sanitizeResetCode(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6)
}

// DRF's throttle text is "… Expected available in N seconds." — and only half translated in ja,
// so the page writes its own sentence and takes just the number.
function throttleMinutes(data: unknown): number | undefined {
  const detail = (data as { detail?: unknown } | undefined)?.detail
  const seconds = typeof detail === 'string' ? Number(detail.match(/(\d+)\s*second/)?.[1]) : NaN
  return seconds > 0 ? Math.ceil(seconds / 60) : undefined
}

function serverText(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined
  const body = data as Record<string, unknown>

  for (const key of ['error', 'detail']) {
    const value = body[key]
    if (typeof value === 'string' && value) return value
  }

  // DRF field errors: {field: ["message", ...]}, or a bare string from a serializer's validate().
  const messages = Object.values(body)
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .filter((m): m is string => typeof m === 'string' && m.length > 0)
  return messages.length ? messages.join(' ') : undefined
}
